import { FPSGame } from './fps3d/FPSGame.js';

declare global {
  interface Window {
    __fpsGame?: FPSGame;
    render_game_to_text?: () => string;
    advanceTime?: (ms: number) => void;
    skipToGameplay?: () => void;
  }
}

function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')
    );
  } catch {
    return false;
  }
}

function showWebGLFallback(): void {
  const loading = document.getElementById('loading-screen');
  if (loading) loading.remove();
  const fallback = document.getElementById('webgl-fallback');
  if (fallback) fallback.style.display = 'flex';
}

function showInitError(message: string, onRetry: () => void): void {
  const loading = document.getElementById('loading-screen');
  if (loading) loading.remove();

  let panel = document.getElementById('game-init-error');
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'game-init-error';
    panel.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:10003',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'justify-content:center',
      'padding:32px',
      'text-align:center',
      'background:radial-gradient(ellipse 80% 60% at 50% 20%, rgba(21,58,85,0.95), #050a14)',
      'color:#eaf8ff',
      'font-family:Inter,system-ui,sans-serif',
    ].join(';');

    const title = document.createElement('h2');
    title.textContent = 'Failed to start Storm Surge';
    title.style.cssText =
      'font-family:Bungee,system-ui,sans-serif;font-size:clamp(24px,5vw,36px);margin-bottom:12px;color:#ff8c8c';

    const detail = document.createElement('p');
    detail.id = 'game-init-error-detail';
    detail.style.cssText =
      'max-width:460px;line-height:1.6;color:rgba(180,200,230,0.9);font-size:15px;margin-bottom:20px';

    const retryBtn = document.createElement('button');
    retryBtn.type = 'button';
    retryBtn.textContent = 'Retry';
    retryBtn.style.cssText =
      'border:none;border-radius:10px;padding:12px 22px;font:700 14px Inter,system-ui,sans-serif;cursor:pointer;background:linear-gradient(180deg,#2eccff,#1a9fd4);color:#041018';

    panel.appendChild(title);
    panel.appendChild(detail);
    panel.appendChild(retryBtn);
    document.body.appendChild(panel);

    retryBtn.addEventListener('click', () => {
      panel!.style.display = 'none';
      onRetry();
    });
  }

  const detailEl = document.getElementById('game-init-error-detail');
  if (detailEl) detailEl.textContent = message;
  panel.style.display = 'flex';
}

function bootGame(mount: HTMLElement): void {
  try {
    const game = new FPSGame(mount);

    window.__fpsGame = game;
    window.render_game_to_text = () => JSON.stringify(game.getTestState());
    window.advanceTime = (ms: number) => game.advanceTime(ms);
    window.skipToGameplay = () => game.skipToGameplay();

    if (new URLSearchParams(location.search).has('autoplay')) {
      window.setTimeout(() => game.skipToGameplay(), 1500);
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Unknown initialization error';
    console.error('[Storm Surge] Game init failed:', err);
    showInitError(msg, () => bootGame(mount));
  }
}

const mount = document.getElementById('game-root');
if (!mount) {
  throw new Error('Missing #game-root container');
}

if (!isWebGLAvailable()) {
  showWebGLFallback();
} else {
  bootGame(mount);
}
